"use strict"

import React, { useState, useEffect, MouseEvent } from 'react'
import EventsProps from './types/EventsProps'
import EventsState from './types/EventsState'
import Filters from './types/Filters'
import DateGroup from './types/DateGroup'
import Event from './Event'

export default ({ events, params, ...filters }: EventsProps) => {
  const searchRef = React.createRef<HTMLInputElement>()

  const initialState: EventsState = {
    enabledFilterGroups: [],
    enabledFilters: { location: [], audience: [], type: [], language: [], series: [] },
    filteredEvents: { 
      week: events.filter(e => e.group == 'week'), 
      threeMonths: events.filter(e => e.group == 'threeMonths'),
    },
    initialLoad: true,
  }

  const [ eventsState, setEventsState ] = useState<EventsState>(initialState)

  const enableFilterGroup = (event: MouseEvent<HTMLElement>, group: string) => {
    let nextState: Array<string>

    if (!eventsState.enabledFilterGroups.includes(group)) {
      nextState = [ ...eventsState.enabledFilterGroups, group ]
    }
    else {
      nextState = eventsState.enabledFilterGroups.filter(g => g !== group)
    }

    setEventsState({
      ...eventsState,
      enabledFilterGroups: nextState,
    })
  }

  const enableFilter = (event: MouseEvent<HTMLElement>, filter: string, group: string) => {
    let filterGroup: Array<string>
    let enabledFilters: Filters
    let results: DateGroup = { week: [], threeMonths: [] }

    if (!eventsState.enabledFilters[group].includes(filter)) {
      filterGroup = [ ...eventsState.enabledFilters[group], filter ]

      enabledFilters = eventsState.enabledFilters
      enabledFilters[group] = filterGroup
    }
    else {
      filterGroup = eventsState.enabledFilters[group].filter(f => f !== filter)

      enabledFilters = eventsState.enabledFilters
      enabledFilters[group] = filterGroup
    }

    if (Object.keys(enabledFilters).length === 0) {
      results.week = events.filter(e => e.group == 'week')
      results.threeMonths = events.filter(e => e.group == 'threeMonths')
    }
    else {
      let filteredEvents = events

      Object.keys(enabledFilters).map(f => {
        if (enabledFilters[f].length > 0) {
          filteredEvents = filteredEvents.filter(e => e[f] && enabledFilters[f].includes(e[f].slug))
        }
      })

      results.week = filteredEvents.filter(e => e.group == 'week')
      results.threeMonths = filteredEvents.filter(e => e.group == 'threeMonths')
    }

    setEventsState({
      ...eventsState,
      enabledFilters: enabledFilters,
      filteredEvents: results
    })
  }

  const enableParamFilters = () => {
    let filteredEvents = events
    let results = { week: [], threeMonths: [] }
    let filterGroups: Array<string> = []

    Object.keys(params).map(f => {
      if (params[f].length > 0) {
        filteredEvents = filteredEvents.filter(e => typeof e[f] !== "undefined" && params[f].includes(e[f].slug))
        filterGroups.push(f)
      }
    })

    results.week = filteredEvents.filter(e => e.group == 'week')
    results.threeMonths = filteredEvents.filter(e => e.group == 'threeMonths')

    setEventsState({
      enabledFilterGroups: filterGroups,
      enabledFilters: params,
      filteredEvents: results,
      initialLoad: false,
    })
  }

  const resetAllFilters = () => {
    setEventsState({
      enabledFilterGroups: [],
      enabledFilters: { location: [], audience: [], type: [], language: [], series: [] },
      filteredEvents: { week: events.filter(e => e.group == 'week'), threeMonths: events.filter(e => e.group == 'threeMonths') },
      initialLoad: false,
    })
  }

  const handleChange = (event) => {
    const term = searchRef.current.value
    let filteredEvents
    let results = { week: [], threeMonths: [] }

    if (term === '') {
      resetAllFilters()
      return
    }

    if (eventsState.filteredEvents.week.length || eventsState.filteredEvents.threeMonths.length) {
      filteredEvents = eventsState.filteredEvents
      results.week = filteredEvents.week.filter(e => e.title.toLowerCase().includes(term.toLowerCase()))
      results.threeMonths = filteredEvents.threeMonths.filter(e => e.title.toLowerCase().includes(term.toLowerCase()))
    }
    else {
      filteredEvents = events.filter(e => e.title.toLowerCase().includes(term.toLowerCase()))
      results.week = filteredEvents.filter(e => e.group == 'week')
      results.threeMonths = filteredEvents.filter(e => e.group == 'threeMonths')
    }

    setEventsState({
      ...eventsState,
      filteredEvents: results
    })
  }

  if (eventsState.initialLoad && 
     (params.location.length || params.audience.length || params.type.length || params.language.length || params.series.length)) {
    enableParamFilters()
  }

  const efg = eventsState.enabledFilterGroups
  const ef = eventsState.enabledFilters
  const fe = eventsState.filteredEvents

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === 'Space') {
      e.preventDefault();
      e.currentTarget.click()
    }
  }

  return (
    <>
    <p>Filter Events By</p>

    {/* Filter Group Toggles */}
    <ul className="filter-groups">
    {Object.keys(filters).map(f => filters[f].length > 0 && (
      <li
        tabIndex={0}
        className={`${efg.includes(f) ? 'tag selected' : 'tag'}`}
        key={f}
        onClick={e => enableFilterGroup!(e, f)}
        onKeyDown={e => handleKeyDown(e)}>{f}</li>
    ))}
      <li
        tabIndex={0}
        onClick={resetAllFilters}
        onKeyDown={e => handleKeyDown(e, -1)}
        className="tag secondary">Reset All Filters</li>
    </ul>

    {/* Filter Groups */}
    <div className="filter-group-container">
    {efg.length > 0 && efg.map(g => (
      <div key={g} className={`filter-group ${g}`}>
        <p className="group-label">{g}</p>
        <ul className="filters">
          { filters[g].map(f => (
            <li
              className={`${ef[g].includes(f.slug) ? 'tag selected' : 'tag'}`}
              key={f.slug}
              id={f.slug}
              tabIndex={0}
              onKeyDown={e => handleKeyDown(e)}
              onClick={e => enableFilter!(e, f.slug, g)}>{f.title}</li>
          ))}
        </ul>
      </div>
    ))}
    </div>

    <div className="form-group">
      <label htmlFor="event-search-input" className="visually-hidden">Search Events</label>
      <input id="event-search-input" type="text" ref={searchRef} onChange={handleChange} />
      <button>Search</button>
    </div>

    {/* Results */}
    <div className="events">
      {fe.week.length > 0 && (
        <h2 className="soon">Events Happening Soon</h2>
      )}
      {fe.week.length > 0 && fe.week.map(e => {
        return <Event event={e} key={e.url} wide={true} />
      })}

      {fe.threeMonths.length > 0 && (
        <h2 className="later">Events Happening Later</h2>
      )}
      {fe.threeMonths.length > 0 && fe.threeMonths.map(e => {
        return <Event event={e} key={e.url} wide={false} />
      })}

      {fe.threeMonths.length === 0 && fe.week.length === 0 && (<h2>No Events Were Found</h2>)}
    </div>
    <a href="/past-events" className="past-events">See past events</a>
    </>
  )
}